import { useNavigate } from "react-router";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import PageMeta from "../common/PageMeta";

interface SalesDetailsProps {
    saleData?: any; // Dados da venda recebidos da API
    isLoading?: boolean;
}

export default function SaleAbandonedDetails({ saleData }: SalesDetailsProps) {
    // Formatação de valores numéricos para exibição
    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value);
    };

    // Formatação de data
    const formatDate = (dateStr: string, showTime: boolean = false) => {
        if (!dateStr) return '--';

        const date = new Date(dateStr);
        const options: Intl.DateTimeFormatOptions = {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            ...(showTime && { hour: '2-digit', minute: '2-digit' })
        };

        return new Intl.DateTimeFormat('pt-BR', options).format(date);
    };

    // Funções utilitárias para formatação
    const formatDocument = (doc: string | null | undefined): string => {
        if (!doc) return '--';
        // Remove todos os caracteres não numéricos
        const numbers = doc.replace(/\D/g, '');

        // Verifica se é CPF ou CNPJ pelo número de dígitos
        if (numbers.length === 11) {
            // Formato CPF: 000.000.000-00
            return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
        } else if (numbers.length === 14) {
            // Formato CNPJ: 00.000.000/0000-00
            return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
        }
        return doc;
    };

    const formatPhone = (phone: string | null | undefined): string => {
        if (!phone) return '--';
        // Remove todos os caracteres não numéricos e o prefixo +55 se existir
        const numbers = phone.replace(/^\+55/, '').replace(/\D/g, '');

        // Verifica se tem 11 dígitos (com DDD e 9) ou 10 dígitos (com DDD sem 9)
        if (numbers.length === 11) {
            return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
        } else if (numbers.length === 10) {
            return numbers.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
        }
        return phone;
    };

    const getWhatsAppLink = (phone: string | null | undefined): string => {
        if (!phone) return '#';
        // Remove tudo exceto números e mantém o código do país se existir
        const numbers = phone.replace(/\D/g, '');
        // Adiciona o código do país (55) se não existir
        const fullNumber = numbers.startsWith('55') ? numbers : `55${numbers}`;
        return `https://wa.me/${fullNumber}`;
    };

    const navigate = useNavigate();

    // Se ainda não temos dados, podemos mostrar um estado vazio ou um placeholder
    if (!saleData) {
        return (
            <>
                <PageMeta
                    title="Sellyo - Detalhes da Venda Abandonada"
                    description="Detalhes da venda abandonada"
                />
                <PageBreadcrumb
                    pageTitle="Detalhes da Venda Abandonada"
                    showBackButton={true}
                    onBack={() => navigate(-1)}
                />
                <div className="text-center p-8">
                    Nenhum dado disponível
                </div>
            </>
        );
    }

    return (
        <>
            <PageMeta
                title="Sellyo - Detalhes da Venda Abandonada"
                description="Detalhes da venda abandonada"
            />

            <PageBreadcrumb
                pageTitle="Detalhes da Venda Abandonada"
                showBackButton={true}
                onBack={() => navigate(-1)}
            />

            <div className="flex flex-col lg:flex-row gap-6">
                {/* Coluna principal com dados da transação e cliente */}
                <div className="w-full lg:w-2/3 flex flex-col gap-6">
                    {/* Bloco de dados do cliente */}
                    <div className="rounded-2xl border border-gray-200 bg-white p-5 md:p-6 dark:border-gray-800 dark:bg-white/[0.03]">
                        <h3 className="text-sm font-medium text-gray-500 uppercase dark:text-gray-400 mb-4">CLIENTE</h3>

                        <div className="flex items-center mb-6">
                            <div className="flex items-center justify-center w-10 h-10 bg-blue-100 text-blue-700 rounded-xl mr-3 dark:bg-blue-900/30 dark:text-blue-400">
                                {saleData.customer.avatar || saleData.customer.name?.[0]?.toUpperCase() || "C"}
                            </div>
                            <div>
                                <p className="text-base font-medium text-gray-900 dark:text-white">{saleData.customer.name}</p>
                                <p className="text-sm text-gray-500 dark:text-gray-400">{saleData.customer.email}</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <div>
                                <h3 className="text-xs font-medium text-gray-500 uppercase dark:text-gray-400">CPF/CNPJ</h3>
                                <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{formatDocument(saleData.customer.document)}</p>
                            </div>
                            <div>
                                <h3 className="text-xs font-medium text-gray-500 uppercase dark:text-gray-400">TELEFONE</h3>
                                <div className="mt-1 flex items-center gap-2">
                                    <p className="text-sm text-gray-700 dark:text-gray-300">
                                        {formatPhone(saleData.customer.phone)}
                                    </p>
                                    {saleData.customer.phone && (
                                        <a
                                            href={getWhatsAppLink(saleData.customer.phone)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center"
                                            title="Abrir conversa no WhatsApp"
                                        >
                                            <img
                                                src="/images/brand/whatsapp.webp"
                                                alt="WhatsApp"
                                                className="h-5 w-5 hover:opacity-80 transition-opacity"
                                            />
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Bloco de dados principais da transação */}
                    <div className="rounded-2xl border border-gray-200 bg-white p-5 md:p-6 dark:border-gray-800 dark:bg-white/[0.03]">
                        <div className="mb-4 flex justify-between">
                            <div>
                                <h3 className="text-sm font-medium text-gray-500 uppercase dark:text-gray-400">VALOR DA TRANSAÇÃO</h3>
                                <p className="mt-3 text-2xl font-bold text-gray-900 dark:text-white">
                                    {formatCurrency(saleData.values.total)}
                                </p>
                            </div>
                        </div>

                        <hr className="my-8 border-gray-300 dark:border-gray-700" />

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                            <div>
                                <h3 className="text-xs font-medium text-gray-500 uppercase dark:text-gray-400">CRIADA EM</h3>
                                <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{formatDate(saleData.cart.createdAt, true)}</p>
                            </div>
                            <div>
                                <h3 className="text-xs font-medium text-gray-500 uppercase dark:text-gray-400">ÚLTIMA ATUALIZAÇÃO</h3>
                                <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{formatDate(saleData.cart.updatedAt, true)}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Coluna lateral com detalhes do carrinho, pagamento e taxas */}
                <div className="w-full lg:w-1/3 flex flex-col gap-6">
                    {/* Bloco de produtos */}
                    <div className="rounded-2xl border border-gray-200 bg-white p-5 md:p-6 dark:border-gray-800 dark:bg-white/[0.03]">
                        <h3 className="text-sm font-medium text-gray-500 uppercase dark:text-gray-400 mb-4">PRODUTO</h3>

                        {/* Produto principal */}
                        {saleData.items && saleData.items.map((item: any, index: number) => (
                            <div key={index} className="flex items-center mb-4">
                                <div className="flex items-center justify-center w-10 h-10 bg-blue-100 text-blue-700 rounded-xl mr-3 dark:bg-blue-900/30 dark:text-blue-400">
                                    {item.title?.[0]?.toUpperCase() || "P"}
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-gray-900 dark:text-white">{item.title}</p>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                        {formatCurrency(item.price)}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Bloco de UTMs */}
                    <div className="rounded-2xl border border-gray-200 bg-white p-5 md:p-6 dark:border-gray-800 dark:bg-white/[0.03]">
                        <h3 className="text-sm font-medium text-gray-500 uppercase dark:text-gray-400 mb-4">RASTREAMENTO</h3>

                        <div className="space-y-2">
                            <div className="grid grid-cols-3 gap-2">
                                <p className="text-sm text-gray-600 dark:text-gray-400 col-span-1">sck</p>
                                <p className="text-sm text-gray-900 dark:text-white col-span-2 break-all whitespace-pre-wrap">{saleData.utm.source || '--'}</p>
                            </div>

                            <div className="grid grid-cols-3 gap-2">
                                <p className="text-sm text-gray-600 dark:text-gray-400 col-span-1">utm_source</p>
                                <p className="text-sm text-gray-900 dark:text-white col-span-2 break-all whitespace-pre-wrap">{saleData.utm.source || '--'}</p>
                            </div>

                            <div className="grid grid-cols-3 gap-2">
                                <p className="text-sm text-gray-600 dark:text-gray-400 col-span-1">utm_medium</p>
                                <p className="text-sm text-gray-900 dark:text-white col-span-2 break-all whitespace-pre-wrap">{saleData.utm.medium || '--'}</p>
                            </div>

                            <div className="grid grid-cols-3 gap-2">
                                <p className="text-sm text-gray-600 dark:text-gray-400 col-span-1">utm_campaign</p>
                                <p className="text-sm text-gray-900 dark:text-white col-span-2 break-all whitespace-pre-wrap">{saleData.utm.campaign || '--'}</p>
                            </div>

                            <div className="grid grid-cols-3 gap-2">
                                <p className="text-sm text-gray-600 dark:text-gray-400 col-span-1">utm_content</p>
                                <p className="text-sm text-gray-900 dark:text-white col-span-2 break-all whitespace-pre-wrap">{saleData.utm.content || '--'}</p>
                            </div>

                            <div className="grid grid-cols-3 gap-2">
                                <p className="text-sm text-gray-600 dark:text-gray-400 col-span-1">utm_term</p>
                                <p className="text-sm text-gray-900 dark:text-white col-span-2 break-all whitespace-pre-wrap">{saleData.utm.term || '--'}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}